import { Injectable } from '@angular/core';
import { SessionService } from '@monsido/ng2/core/session/session.service';
import { MonTableCollection } from '@monsido/ng2/models/table-collection.interface';
import { ApiService } from '../api-service/api.service';
import { BaseRepo, BaseRepoRequestType } from '../base-repo/base-repo';
import { PageFixModel } from './page-fix-model';
import { PAGE_FIX_FIXABLE_TYPES } from '@client/app/modules/page-fix/constants/page-fix-fixable-types';

export type PageFixFixableReceiveType = 'AccessibilityCheck' | 'AccessibilitySourceCode' | 'Link' | 'SpellingError' | 'Custom';
export type PageFixFixableSendType = typeof PAGE_FIX_FIXABLE_TYPES[keyof typeof PAGE_FIX_FIXABLE_TYPES];
export type PageFixFixableRequestType = {
    fixable_type?: PageFixFixableSendType,
    fixable_id?: number,
};
type PageFixChangeType = {
    change: {
        value: string,
        attribute?: string,
        check_id?: number,
    }
    selector?: string,
    change_type?: string,
};

export type PageFixType = PageFixChangeType & {
    fixable_type?: PageFixFixableReceiveType,
    fixable_id?: number,
    custom_selector: string | string[] | null,
    created_at: string,
    customer_id: number,
    id: number,
    updated_at: string,
    selector: string,
};

type PageFixCreateType = PageFixChangeType & PageFixFixableRequestType & {
    selector: string,
};

@Injectable({
    providedIn: 'root',
})
export class PageFixRepoService extends BaseRepo {

    constructor (protected sessionService: SessionService, private apiService: ApiService) {
        super();
    }

    getAll (params: BaseRepoRequestType & PageFixFixableRequestType): Promise<MonTableCollection<PageFixModel>> {
        params = params || {};
        return this.apiService.get<MonTableCollection<PageFixType>>('domains/' + this.getDomainId(params.domain_id) + '/page_fixes', params)
            .then((res) => this.toModels(res));
    }

    create (params: BaseRepoRequestType & PageFixCreateType , contextToken): Promise<PageFixModel> {
        params = params || {};
        params = this.setGroupId(params);
        return this.apiService.post<PageFixType>('domains/' + this.getDomainId(params.domain_id) + '/page_fixes', params, contextToken)
            .then((res) => this.toModel(res));
    }

    update (pageFix: PageFixType, data: PageFixChangeType, params: BaseRepoRequestType): Promise<PageFixModel> {
        params = params || {};
        params = this.setGroupId(params);
        return this.apiService.patch<PageFixType>('domains/' + this.getDomainId(params.group_id) + '/page_fixes/' + pageFix.id, data, params)
            .then((res) => this.toModel(res));
    }

    destroy (id: number): Promise<void> {
        return this.apiService.delete<void>('domains/' + this.getDomainId() + '/page_fixes/' + id);
    }

    deleteAll (params: BaseRepoRequestType & PageFixFixableRequestType): Promise<void> {
        params = params || {};
        return this.apiService.delete<void>('domains/' + this.sessionService.getDomainId(params) + '/page_fixes/', params);
    }

    private toModels (models: MonTableCollection<PageFixType>): MonTableCollection<PageFixModel> {
        for (let i = 0; i < models.length; i++) {
            models[i] = this.toModel(models[i]);
        }
        return models as unknown as MonTableCollection<PageFixModel>;
    }

    private toModel (model: PageFixType): PageFixModel {
        return new PageFixModel(model);
    }
}
